import * as Sentry from '@sentry/browser';
import { fetchCheckout, fetchData } from '../utils/async';
import { getCookie, removeEvent, addEvent } from '../utils/dom';
import { prefix, translateObjectData } from '../utils/locale';
import { deepClone } from '../utils/object';
import { fieldValidation, urlify } from '../utils/string';
import { paymentMethodsMapping } from '../config';

export const SUBSCRIBED = 'SUBSCRIBED';
export const ADD_VOUCHER = 'ADD_VOUCHER';
export const VOUCHER_INPUT = 'VOUCHER_INPUT';
export const VOUCHER_ERROR = 'VOUCHER_ERROR';
export const ADD_TO_BAG_ERROR = 'ADD_TO_BAG_ERROR';
export const LIST_ITEM_CACHE = 'LIST_ITEM_CACHE';
export const PRE_SELECT_SIZE = 'PRE_SELECT_SIZE';
export const SELECT_SIZE = 'SELECT_SIZE';
export const SELECT_QUANTITY = 'SELECT_QUANTITY';
export const UPDATE_SELECTION_CACHE = 'UPDATE_SELECTION_CACHE';
export const UPDATE_SELECTION = 'UPDATE_SELECTION';
export const UPDATE_BUDBEE = 'UPDATE_BUDBEE';
export const GET_ADDRESS = 'GET_ADDRESS';
export const UPDATE_ADDRESS = 'UPDATE_ADDRESS';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const DELIVERY_PROCEED = 'DELIVERY_PROCEED';
export const KLARNA_LOADED = 'KLARNA_LOADED';
export const KLARNA_UNLOADED = 'KLARNA_UNLOADED';
export const ADYEN_INITIATED = 'ADYEN_INITIATED';
export const STRIPE_INITIATED = 'STRIPE_INITIATED';
export const STRIPE_PAYMENT_INTENTS_INITIATED = 'STRIPE_PAYMENT_INTENTS_INITIATED';
export const PAYMENT_UPDATING = 'PAYMENT_UPDATING';
export const STATE_ERROR = 'STATE_ERROR';
export const SELECTION_ERROR = 'SELECTION_ERROR';
export const SELECTION_ERROR_STOCK = 'SELECTION_ERROR_STOCK';
export const INITIATED_SCRIPT = 'INITIATED_SCRIPT';
export const BOOLEAN_CHANGE = 'BOOLEAN_CHANGE';
export const PAYMENT_RETURN = 'PAYMENT_RETURN';
export const DESTROY_SELECTION = 'DESTROY_SELECTION';
export const ERROR_NOTIFICATION = 'ERROR_NOTIFICATION';
export const SUCCESS_NOTIFICATION = 'SUCCESS_NOTIFICATION';

const removeInvalidPaymentFields = (setup, paymentData, useShippingAddress, keepPassword = false) => {
	const { country, countries } = setup;
	const whitelistedFields = {
		level0: ['paymentMethod', 'shippingMethod', 'consents', 'affiliate', 'additionalNotes', 'additionalInformation', 'customerClubFields', 'cartAbandonmentEmail', 'accountNumber', 'additionalFields', 'paymentReturnPage', 'paymentFailedPage', 'termsAndConditions', 'address', 'shippingAddress'],
		address: ['newsletter', 'firstName', 'lastName', 'email', 'password', 'phoneNumber', 'company', 'vatNumber', 'address1', 'address2', 'zipCode', 'state', 'city', 'country'],
		shippingAddress: ['firstName', 'lastName', 'email', 'phoneNumber', 'company', 'address1', 'address2', 'zipCode', 'city', 'state', 'country'],
	};

	// Copy only whitelisted fields.
	const returnData = Object.keys(paymentData).reduce((acc, f) => {
		if (whitelistedFields.level0.indexOf(f) === -1) return acc;
		if (whitelistedFields[f]) {
			// Add shippingAddress only if exists
			if (f === 'shippingAddress' && !useShippingAddress) return acc;

			// check second level.
			return {
				...acc,
				[f]: Object.keys(paymentData[f]).reduce((acc1, f1) => {
					if (whitelistedFields[f].indexOf(f1) === -1 || (!keepPassword && f1 === 'password')) return acc1;
					return { ...acc1, [f1]: paymentData[f][f1] };
				}, {}),
			};
		}
		if (typeof paymentData[f] === 'object') return { ...acc, [f]: deepClone(paymentData[f]) };
		return { ...acc, [f]: paymentData[f] };
	}, {});

	// Add required fields
	if (useShippingAddress) {
		returnData.shippingAddress.country = returnData.shippingAddress.country || country;
		if (!returnData.shippingAddress.phoneNumber && returnData.address.phoneNumber) {
			returnData.shippingAddress.phoneNumber = returnData.address.phoneNumber;
		}
	}
	returnData.address.country = returnData.address.country || country;

	let additionalNotes = '';
	if (returnData.additionalInformation) {
		additionalNotes = returnData.additionalInformation.map((info) => info?.message).join('\n');
		delete returnData.additionalInformation;
	}
	if (additionalNotes) returnData.additionalNotes = additionalNotes;

	// Check that state is correct and applicable else remove.
	if (!(returnData.address.state && countries?.[returnData.address.country]?.states?.[returnData.address.state])) delete returnData.address.state;
	// Check that state is correct and applicable else remove.
	if (useShippingAddress && !(returnData.shippingAddress.state && countries?.[returnData.shippingAddress.country]?.states?.[returnData.shippingAddress.state])) delete returnData.shippingAddress.state;

	returnData.paymentFailedPage = `${window.location.origin}${prefix(setup, 'selection/failed')}`;
	returnData.paymentReturnPage = `${window.location.origin}${prefix(setup, 'selection/receipt')}`;

	return returnData;
};

export const clientInit = () => ({ type: 'CLIENT_INIT' });
export const retrieveProductCache = () => ({ type: 'RETRIEVE_PRODUCT_CACHE' });

export const subscribeKlaviyo = (details) => (dispatch, getState) => {
	const { market = 0 } = getState().environment.setup;
	let pathName = window.location.pathname;
	if (pathName.indexOf('i2') !== -1 && market !== 0 && market !== 19 && market !== 32) pathName += '-rw';

	return (
		fetchData('klaviyo/subscribe', { ...details, tokenId: pathName }).then((response) => {
			if (response?.data?.interactionToken) {
				dispatch(destroySelection());
				window.location.href = `${window.location.origin}/campaign/${response.data.interactionToken}${pathName}`;
				return true;
			}
			return dispatch({ type: SUBSCRIBED, data: response });
		}).catch((err) => console.log(err))
	);
};

export const subscribeToNewsletter = (details, campaign = false) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;

	return fetchCheckout(`/newsletter-subscription/${details.email}`, token, [], 'POST').then(() => {
		if (campaign) dispatch(subscribeKlaviyo(details));
		return dispatch({ type: SUBSCRIBED });
	}).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: error when subscribing to newsletter ', err);
	});
};

export const preSelectSize = (item) => ({ type: PRE_SELECT_SIZE, item });
export const updateListItemCache = (productId, listName) => ({ type: LIST_ITEM_CACHE, productId, listName });
export const selectSize = (product, item, quantity = 1) => ({ type: SELECT_SIZE, product, item, quantity });

export const productAlert = ({ selected }) => (dispatch, getState) => {
	const { email } = getState().checkout.payment.address;
	const { token } = getState().checkout.selection;
	const { setup } = getState().environment;
	const { country, language } = setup;
	// dispatch(subscribeToNewsletter({ email }));

	return fetchCheckout('/back-in-stock-subscription', token, { email, shipTo: { country }, item: selected, language }, 'POST').then(() => (
		dispatch({ type: SUBSCRIBED })
	)).catch((err) => console.log('DEBUG: error when subscribing to newsletter ', err));
};

export const addToBag = (item, quantity = 1, category, cachedItem) => (dispatch, getState) => {
	const { country = '', countries = {}, viewerState = false } = getState().environment.setup;
	const token = getState().checkout.selection.token;
	const product = item.slice(0, item.indexOf('-'));
	const cache = deepClone(cachedItem);
	cache.category = category;

	return (
		fetchCheckout(`/items/${item}/quantity/${quantity}`, token, [], 'POST').then((data) => {
			if (data.errors) return dispatch({ type: ADD_TO_BAG_ERROR });

			dispatch({ type: UPDATE_SELECTION_CACHE, product, productCache: cache });
			dispatch({ type: UPDATE_SELECTION, data });

			return data;
		}).then((data) => {
			if (country !== data.location.country) {
				if (countries?.[country].states?.[viewerState]) dispatch(changeState(viewerState, country));
				else if (countries?.[country].states) dispatch(changeState(country === 'US' ? 'NY' : Object.keys(countries?.[country].states)[0], country));
				else dispatch(changeCountry(country));

				dispatch(changeAddressInput('address', 'country', country));
				dispatch(changeAddressInput('shippingAddress', 'country', country));
			} else if (viewerState !== data.location.state && countries?.[country].states?.[viewerState]) {
				dispatch(changeState(viewerState));
			}
			dispatch({ type: 'OPEN_CART' });

			return data;
		}).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const changeQuantity = (product, line, quantity) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const allItems = getState().checkout.selection.items;
	const totalProducts = allItems.reduce((acc, item) => (item.item.indexOf(product) === 0 ? acc + 1 : acc), 0);
	if (quantity === 0 && totalProducts <= 1) {
		// Remove from selection cache.
		dispatch({ type: UPDATE_SELECTION_CACHE, product });
	}
	return (
		fetchCheckout(`/lines/${line}/quantity/${quantity}`, token, [], 'PUT').then((data) => (
			dispatch({ type: UPDATE_SELECTION, data })
		)).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const changeSize = (product, line, quantity, newItem) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;

	// Remove previous product
	return fetchCheckout(`/lines/${line}/quantity/0`, token, [], 'PUT').then(() => (
		fetchCheckout(`/items/${newItem}/quantity/${quantity}`, token, [], 'POST')
	)).then((data) => (
		dispatch({ type: UPDATE_SELECTION, data })
	)).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const changeCountry = (country) => (dispatch, getState) => {
	const newCountry = getState().environment.setup.countries[country];
	const market = getState().environment.campaign ? getState().environment.campaign.market : newCountry.market;
	const token = getState().checkout.selection.token;
	const { state = '' } = getState().checkout.payment.shippingAddress;
	dispatch({ type: 'UPDATE_COUNTRY', country: newCountry.country, market, pricelist: newCountry.pricelist });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });
	return (
		fetchCheckout(`/countries/${newCountry.country}`, token, [], 'PUT').then((data) => {
			if (newCountry?.states?.[state]) dispatch(changeState(state));
			return dispatch({ type: UPDATE_SELECTION, data });
		}).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const changeState = (state, country = false) => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	if (!country) country = getState().environment.setup.country;
	return (
		fetchCheckout(`/countries/${country}/states/${state}`, token, [], 'PUT').then((data) => {
			if (data.errors) return dispatch({ type: STATE_ERROR });
			dispatch({ type: UPDATE_ADDRESS, group: 'address', id: 'state', value: state });
			dispatch({ type: UPDATE_ADDRESS, group: 'shippingAddress', id: 'state', value: state });

			return dispatch({ type: UPDATE_SELECTION, data });
		}).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const changePaymentMethod = (method) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { adyenInitiated } = getState().checkout;

	return (
		fetchCheckout(`/payment-methods/${method}`, token, [], 'PUT').then((data) => {
			if (adyenInitiated) window.location = window.location.href;
			return dispatch({ type: UPDATE_SELECTION, data });
		}).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const changeShippingMethod = (method) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	return (
		fetchCheckout(`/shipping-methods/${method}`, token, [], 'PUT').then((data) => (
			dispatch({ type: UPDATE_SELECTION, data })
		)).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
		})
	);
};

export const checkZipCode = () => (dispatch, getState) => {
	const { zipCode = '', country } = getState().checkout.payment.address;
	const { selection } = getState().checkout;

	if (/\S/.test(zipCode)) {
		fetchData('budbee', { country, zipCode }).then(({ success, errors = false }) => {
			if (!success && selection.shippingMethod && selection.shippingMethod.toLowerCase().indexOf('budbee') !== -1) {
				const otherShippingMethods = selection.shippingMethods.filter((method) => method.shippingMethod.toLowerCase().indexOf('budbee') === -1);
				if (otherShippingMethods.length > 0) dispatch(changeShippingMethod(otherShippingMethods[0].shippingMethod));
			}
			dispatch({ type: DELIVERY_PROCEED });
			return dispatch({ type: UPDATE_BUDBEE, success, errors });
		}).catch((err) => {
			Sentry.captureException(err);
			console.log(err);
		});
	}
};

export const getAddress = () => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { identityNumber = '' } = getState().checkout.payment.address;
	const { paymentMethod = '' } = getState().checkout.selection;
	const error = fieldValidation(identityNumber, 'identityNumber');

	if (error || paymentMethod === '') {
		return dispatch({ type: GET_ADDRESS, success: false });
	}

	return fetchCheckout('/address-search', token, { identityNumber, paymentMethod }, 'POST').then((data) => (
		dispatch({ type: GET_ADDRESS, success: true, data })
	)).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: error when subscribing to newsletter ', err);
	});
};

export const changeAddressInput = (group, id, value) => ({ type: UPDATE_ADDRESS, group, id, value });

export const booleanChange = (id) => ({ type: BOOLEAN_CHANGE, id });

export const addVoucher = () => (dispatch, getState) => {
	const currentValue = getState().checkout.vouchers.currentValue;
	const token = getState().checkout.selection.token;

	if (fieldValidation(currentValue, 'voucher')) return dispatch({ type: VOUCHER_ERROR });

	return fetchCheckout('/vouchers', token, { voucher: currentValue }, 'POST').then((data) => {
		if (data.errors) return dispatch({ type: VOUCHER_ERROR });

		dispatch({ type: VOUCHER_INPUT, value: '' });
		return dispatch({ type: UPDATE_SELECTION, data });
	}).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const removeVoucher = (voucherId) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	return fetchCheckout(`/vouchers/${voucherId}`, token, {}, 'DELETE').then((data) => (
		dispatch({ type: UPDATE_SELECTION, data })
	)).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const voucherInputChange = (value) => ({ type: VOUCHER_INPUT, value });

export const initiatedScript = () => ({ type: INITIATED_SCRIPT });

export const initiateKlarna = () => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { setup } = getState().environment;
	const paymentData = {};
	paymentData.paymentFailedPage = `${window.location.origin}${prefix(setup, 'selection/failed')}`;
	paymentData.paymentReturnPage = `${window.location.origin}${prefix(setup, 'selection/receipt')}`;
	paymentData.termsAndConditions = true;

	return fetchCheckout('/payment', token, paymentData, 'POST').then((data) => (
		dispatch({ type: 'INITIATE_SELECTION', data })
	)).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const klarnaLoaded = () => ({ type: KLARNA_LOADED });

export const klarnaUnloaded = () => ({ type: KLARNA_UNLOADED });

export const updateKlarna = () => (dispatch, getState) => {
	const { selection, payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const paymentData = removeInvalidPaymentFields(setup, payment, useShippingAddress);

	delete paymentData.termsAndConditions;

	if (window.CentraCheckout) {
		window.CentraCheckout.suspend();
	}

	return fetchCheckout('/payment-fields', selection.token, paymentData, 'PUT').then((data) => (
		dispatch({ type: PAYMENT_RETURN, data })
	)).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const clearKlarna = () => ({ type: PAYMENT_RETURN });

const stripeIntentsUpdate = (event, responseType) => (dispatch) => {
	const { shippingCountry, shippingState, shippingMethod } = event.detail;

	const selectionUpdated = (response) => {
		const data = response.data;

		let returnObject = {};
		if (data && data.selection && data.selection.totals) {
			returnObject = {
				country: data.location.country,
				currency: data.selection.currency,
				currencyDenominator: data.selection.currencyFormat.denominator,
				grandTotalPriceAsNumber: data.selection.totals.grandTotalPriceAsNumber,
				shippingMethod: data.selection.shippingMethod,
				shippingMethodsAvailable: data.shippingMethods,
			};
		} else {
			returnObject = {
				error: true,
			};
		}

		const shippingUpdateEvent = new CustomEvent(responseType, { detail: returnObject }); // eslint-disable-line no-undef
		document.dispatchEvent(shippingUpdateEvent);
		return response;
	};

	if (responseType === 'centra_checkout_shipping_method_response') {
		return dispatch(changeShippingMethod(shippingMethod)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
	}

	if (shippingState) {
		return dispatch(changeState(shippingState, shippingCountry)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
	}

	return dispatch(changeCountry(shippingCountry)).then(selectionUpdated).catch((err) => console.log('DEBUG: err', err));
};

const paymentSelected = (event) => (dispatch, getState) => {
	const { selection } = getState().checkout;
	let { payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { paymentMethodSpecificFields, paymentMethod, responseEventRequired, addressIncluded, billingAddress, shippingAddress } = event.detail;
	if (paymentMethod === 'stripe-payment-intents' && addressIncluded) {
		payment = { ...payment, address: { ...billingAddress }, shippingAddress: { ...shippingAddress } };
		useShippingAddress = true;
	}
	const paymentData = removeInvalidPaymentFields(setup, payment, useShippingAddress);
	const postData = {
		paymentMethod,
		paymentMethodSpecificFields,
		...paymentData,
	};

	// Prevent extra clicks while payment is updating.
	dispatch({ type: PAYMENT_UPDATING, isUpdating: true });

	if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'Pay',
		ecommerce: {
			checkout: {
				actionField: { step: 4, option: paymentMethodSpecificFields?.paymentMethod?.brand },
				products: selection.items.map((item) => ({
					name: item.name,
					id: item.id.replace(/p(\d+)-v.*/, '$1'),
					sku: item.sku,
					price: item.priceEachAsNumber,
					brand: item.brand,
					category: item.category,
					variant: item.variant,
					quantity: item.quantity,
				})),
			},
		},
	});

	window.dataLayer.push({ ecommerce: null });
	window.dataLayer.push({
		event: 'add_payment_info',
		ecommerce: {
			currency: setup.currency,
			value: selection.totals.grandTotalPriceAsNumber,
			coupon: selection.discounts.vouchers.reduce((acc, obj) => [...acc, obj.voucher], []).join(', '),
			payment_type: paymentMethodSpecificFields?.paymentMethod?.type || paymentMethodSpecificFields?.paymentMethod?.brand || paymentMethod,
			items: selection.items.map((item) => {
				const localizedItem = translateObjectData(setup, item);
				const itemCategory = localizedItem.category && localizedItem.category.split('/').reduce((acc, category, j) => ({ ...acc, [`item_category${j > 0 ? j + 1 : ''}`]: category }), {});

				return {
					item_id: localizedItem.id.replace(/p(\d+)-v.*/, '$1'),
					item_name: localizedItem.name,
					item_brand: localizedItem.brand,
					...(itemCategory || {}),
					item_variant: localizedItem.variant,
					price: localizedItem.priceEachAsNumber,
					quantity: localizedItem.quantity,
				};
			}),
			user_data: {
				email: paymentData?.address?.email,
				address: {
					first_name: paymentData?.address?.firstName,
					last_name: paymentData?.address?.lastName,
					street: paymentData?.address?.address1,
					postal_code: paymentData?.address?.zipCode,
					region: paymentData?.address?.state,
					city: paymentData?.address?.city,
					country: paymentData?.address?.country,
				},
			},
		},
	});

	return fetchCheckout('/payment', selection.token, postData, 'POST')
		.then((response) => {
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

			if (response.action === 'redirect') {
				window.location.href = response.url;
				return true;
			}

			if (response.errors) {
				// Payment failed for some reason, show error
				return dispatch({ type: 'SELECTION_ERROR', hasError: true });
			}

			if (response.action === 'redirect') {
				window.location.href = response.url;
				return true;
			}

			if (response.action === 'success') {
				const { language = '' } = setup;
				const extraData = {};
				if (language !== '' && language !== 'en') extraData.language = language;

				// Remove events set up earlier.
				removeEvent(document, 'centra_checkout_payment_callback.adyenDropIn');
				removeEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents');
				removeEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents');
				removeEvent(document, 'centra_checkout_payment_callback.stripeIntents');

				dispatch({ type: 'RECEIPT', payload: { path: 'selection/receipt', ...extraData } });
				window.scrollTo(0, 0);
				setTimeout(() => {
					dispatch({ type: 'RECEIPT_DATA', data: response });
				}, 500);
				return true;
			}

			if (responseEventRequired) {
				// action is javascript
				if (response.formFields) {
					const detail = response.formFields;
					const updateEvent = new CustomEvent('centra_checkout_payment_response', { detail }); // eslint-disable-line no-undef
					document.dispatchEvent(updateEvent);
				} else {
					return dispatch({ type: 'SELECTION_ERROR', hasError: true });
				}
			}

			return Promise.reject(new Error('Adyen Drop In listener failed.'));
		}).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: err', err);
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });
		});
};

export const purchase = (paymentInitiateOnly = false, setPaymentMethod = '') => (dispatch, getState) => {
	const { selection, payment, useShippingAddress } = getState().checkout;
	const { setup } = getState().environment;
	const { token } = selection;
	const { language, countries } = setup;
	const paymentData = removeInvalidPaymentFields(setup, payment, useShippingAddress);
	const hasErrors = [];

	if (!paymentInitiateOnly) {
		if (!paymentData.address.state && countries?.[paymentData.address.country]?.states) {
			// Missing state
			dispatch({ type: 'UPDATE_ERRORS', group: 'address', id: 'state', error: true });
			hasErrors.push('address_state');
		}

		if (hasErrors.length > 0) {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			window.dataLayer.push({ ecommerce: null });
			window.dataLayer.push({
				event: 'PurchaseProceedError',
				ecommerce: {
					checkout: {
						actionField: { step: 2, option: hasErrors.join(', ') },
						products: selection.items.map((item) => ({
							name: item.name,
							id: item.id.replace(/p(\d+)-v.*/, '$1'),
							sku: item.sku,
							price: item.priceEachAsNumber,
							brand: item.brand,
							category: item.category,
							variant: item.variant,
							quantity: item.quantity,
						})),
					},
				},
			});
			// console.log('DEBUG: hasErrors', hasErrors);
			return dispatch({ type: SELECTION_ERROR, hasError: true });
		}

		const marsbladeCookie = getCookie('mbp', true);
		if (marsbladeCookie !== null) {
			paymentData.additionalNotes = `Key,Value\r\n${marsbladeCookie.map((param) => param.map((p) => urlify(p)).join(',')).join('\r\n')}`;
		}

		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({
			event: 'add_shipping_info',
			ecommerce: {
				currency: setup.currency,
				value: selection.totals.grandTotalPriceAsNumber,
				coupon: selection.discounts.vouchers.reduce((acc, obj) => [...acc, obj.voucher], []).join(', '),
				shipping_tier: selection?.shippingMethodName || '',
				items: selection.items.map((item) => {
					const localizedItem = translateObjectData(setup, item);
					const itemCategory = localizedItem.category && localizedItem.category.split('/').reduce((acc, category, j) => ({ ...acc, [`item_category${j > 0 ? j + 1 : ''}`]: category }), {});

					return {
						item_id: localizedItem.sku,
						item_name: localizedItem.name,
						item_brand: localizedItem.brand,
						...(itemCategory || {}),
						item_variant: localizedItem.variant,
						price: localizedItem.priceEachAsNumber,
						quantity: localizedItem.quantity,
					};
				}),
				user_data: {
					email: paymentData?.address?.email,
					address: {
						first_name: paymentData?.address?.firstName,
						last_name: paymentData?.address?.lastName,
						street: paymentData?.address?.address1,
						postal_code: paymentData?.address?.zipCode,
						region: paymentData?.address?.state,
						city: paymentData?.address?.city,
						country: paymentData?.address?.country,
					},
				},
			},
		});
	} else {
		if (!paymentData.address.state && countries?.[paymentData.address.country]?.states) {
			paymentData.address.state = 'NY';
		}
		paymentData.paymentInitiateOnly = true;
		paymentData.termsAndConditions = true;
		paymentData.paymentMethod = setPaymentMethod;
	}

	return fetchCheckout('/payment', token, paymentData, 'POST').then((data) => {
		if (data.action === 'redirect') {
			// Redirect to new URL
			window.location = data.url;
			return false;
		}
		if (data.action === 'form') {
			// Return to checkout to display form

			if (data.formType === 'adyen-drop-in') {
				const $checkoutContainer = document.getElementById('adyenDropInContainer');
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: ADYEN_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

				removeEvent(document, 'centra_checkout_payment_callback.adyenDropIn');
				addEvent(document, 'centra_checkout_payment_callback.adyenDropIn', (event) => {
					if (event.detail.paymentMethod.indexOf(paymentMethodsMapping.adyenDropIn) !== -1) dispatch(paymentSelected(event));
				});
				return false;
			}

			if (data.formType === 'stripe-checkout') {
				const $checkoutContainer = document.getElementById('stripeCheckoutContainer');
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: STRIPE_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

				return false;
			}

			if (data.formType === 'stripe-payment-intents') {
				const $checkoutContainer = document.getElementById('stripePaymentIntentsContainer');
				window.stripeRequestButtonSelector = '#stripeCheckoutButton';
				$checkoutContainer.innerHTML = data.formHtml;
				evaluate($checkoutContainer);
				dispatch({ type: STRIPE_PAYMENT_INTENTS_INITIATED });
				dispatch({ type: PAYMENT_UPDATING, isUpdating: false });

				removeEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents');
				addEvent(document, 'centra_checkout_shipping_address_callback.stripeIntents', (event) => (
					dispatch(stripeIntentsUpdate(event, 'centra_checkout_shipping_address_response'))
				));
				removeEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents');
				addEvent(document, 'centra_checkout_shipping_method_callback.stripeIntents', (event) => (
					dispatch(stripeIntentsUpdate(event, 'centra_checkout_shipping_method_response'))
				));
				removeEvent(document, 'centra_checkout_payment_callback.stripeIntents');
				addEvent(document, 'centra_checkout_payment_callback.stripeIntents', (event) => {
					if (event.detail.paymentMethod.indexOf(paymentMethodsMapping.stripePaymentIntents) !== -1) dispatch(paymentSelected(event));
				});
				return false;
			}

			const d = document.createElement('div');
			d.innerHTML = data.formHtml;
			document.body.appendChild(d);
			d.querySelector('form').submit();
			return false;
		}
		if (data.action === 'success') {
			// Purchase complete navigate to receipt.
			dispatch({ type: 'RECEIPT', payload: { language } });
			window.scrollTo(0, 0);
			setTimeout(() => {
				dispatch({ type: 'RECEIPT_DATA', data });
			}, 500);
			return true;
		}
		if (data.errors && data.errors.stock) {
			dispatch({ type: UPDATE_SELECTION, data });
			dispatch({ type: PAYMENT_UPDATING, isUpdating: false });
			return dispatch({ type: SELECTION_ERROR_STOCK, hasError: true });
		}
		return dispatch({ type: SELECTION_ERROR, hasError: true });
	}).catch((err) => {
		Sentry.captureException(err);
		console.log('DEBUG: err', err);
	});
};

export const updatePaymentMethod = () => (dispatch, getState) => {
	const { adyenInitiated = false, stripeInitiated = false, stripePaymentIntentsInitiated = false } = getState().checkout;
	if (stripePaymentIntentsInitiated) {
		dispatch({ type: PAYMENT_UPDATING, isUpdating: true });
		dispatch(purchase(true, paymentMethodsMapping.stripePaymentIntents));
	}
	if (adyenInitiated || stripeInitiated) {
		dispatch({ type: PAYMENT_UPDATING, isUpdating: true });
		dispatch(purchase());
	} else {
		// Here we want to just update payment-fields irrespective of what payment method we are using
		// This is so that we can update Centra with information that has been added to the checkout
		// If an email has been added or if the state has changed.
		dispatch(updateKlarna());
	}
};

const evaluate = (subtree, context = window) => {
	function getSnippetType(script) {
		return script && (script.type || '');
	}
	function getRawSnippet(script) {
		return script && (script.text || script.textContent || script.innerHTML || '');
	}

	if (subtree) {
		try {
			const scripts = subtree.getElementsByTagName('script');
			const scriptsAsArray = Array.from(scripts).filter((script) => getSnippetType(script) !== 'application/json');

			scriptsAsArray.forEach((script) => {
				const unescaped = getRawSnippet(script).replace(/\\/gi, '');
				eval.call(context, unescaped); // eslint-disable-line no-eval
			});
		} catch (error) {
			console.error('Caught an error in evaluate function:', error);
		}
	}
};

export const destroySelection = () => (dispatch) => {
	// const { country, countries } = getState().environment.setup;
	// const defaultCountrySettings = countries[country];
	// Clear possible campaign market/pricelist to keep in sync with Centra.
	// dispatch({ type: 'UPDATE_COUNTRY', country, market: parseInt(defaultCountrySettings.market, 10), pricelist: parseInt(defaultCountrySettings.pricelist, 10) });
	// dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	dispatch({ type: DESTROY_SELECTION });
};
